import React, { useEffect, useState } from 'react';
import { post } from '../httpService';
import { formatDateTime } from '../utils/util_function';
import { useForm } from 'react-hook-form';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { LinkIcon, TrashIcon } from '@heroicons/react/24/outline';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
);

const CreateLink: React.FC = () => {
    const [paymentLinkData, setPaymentLinkData] = useState<any>([]);
    const [isLoad, setIsLoad] = useState<boolean>(true);
    const [expandedRow, setExpandedRow] = useState<string | null>(null);
    const [cashOutIsLoad, setCashOutIsLoad] = useState<boolean>(false);
    const [imagePreview, setImagePreview] = useState<string | null>(null); // Aperçu de l'image

    const { register, handleSubmit, reset, formState: { isSubmitting }, watch } = useForm({
        defaultValues: {
            amount: '',
            description: '',
            image: null,
        }
    });

    const getpaymentLink = () => {
        post('/payment-link/list-payment-link', null)
            .then((response) => {
                setPaymentLinkData(response.data.data['data']);
                setIsLoad(false);
            })
            .catch((error) => {
                console.error('Erreur GET :', error);
            });
    }

    useEffect(() => {
        getpaymentLink();
    }, []);

    const toggleRowExpansion = (groupId: string) => {
        if (expandedRow === groupId) {
            setExpandedRow(null);
        } else {
            setExpandedRow(groupId);
        }
    };

    const cancel = () => {
        reset();
        setImagePreview(null);  // Réinitialiser l'aperçu de l'image
    };

    // Fonction d'envoi du formulaire avec FormData
    const onSubmit = (data: any) => {
        const formData = new FormData();
        formData.append('amount', data.amount);
        formData.append('description', data.description);
        if (data.image && data.image[0]) {
            formData.append('image', data.image[0]);  // Ajouter l'image au FormData
        }

        setCashOutIsLoad(true);

        post('/payment-link/create-payment-link', formData)
            .then((response) => {
                getpaymentLink();
                setCashOutIsLoad(false);
                reset();
                setImagePreview(null);  // Réinitialiser l'aperçu après la soumission
            })
            .catch((error) => {
                setCashOutIsLoad(false);
                console.error('Erreur de création de lien de paiement :', error);
            });
    };

    const deletePaymentLink = (uuid: any) => {

        var postData = {
            'payment_link_uuid': uuid,
        }

        setIsLoad(true);

        post('/payment-link/delete-link', postData)
            .then((response) => {
                getpaymentLink();
                setIsLoad(false);
            })
            .catch((error) => {
                console.error('Erreur GET :', error);
            });
    }


    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result as string); // Met à jour l'aperçu de l'image
            };
            reader.readAsDataURL(file);
        } else {
            setImagePreview(null); // Réinitialiser si aucun fichier n'est sélectionné
        }
    };

    const copyToClipboard = (link: string) => {
        navigator.clipboard.writeText(link).then(() => {
            alert('Lien copié dans le presse-papier !');
        }).catch((error) => {
            console.error('Erreur lors de la copie du lien :', error);
        });
    };


    return (
        <div className="p-5">
            <div className="card border p-5 mb-10 rounded border-grey bg-white">
                <h1>Accueil / links / Create</h1>
                <h1 className="text-lg">Link account</h1>
            </div>

            <div className="lg:flex lg:space-x-2">
                <div className="lg:w-2/3 card border p-5 rounded border-grey bg-white">
                    <h1 className="text-lg font-bold mb-2">Créer un lien de paiement</h1>
                    <div className="bg-white p-5 rounded border-grey border shadow-sm w-full">
                        <form onSubmit={handleSubmit(onSubmit)} encType="multipart/form-data">
                            <div className="mb-4">
                                <label className="block mb-1">Montant</label>
                                <input
                                    type="text"
                                    className="w-full p-2 border border-gray-light bg-grey10 rounded"
                                    {...register('amount', { required: true })}
                                />
                            </div>

                            <div className="mb-4">
                                <label className="block mb-1">Description</label>
                                <input
                                    type="text"
                                    className="w-full p-2 border border-gray-light bg-grey10 rounded"
                                    {...register('description', { required: true })}
                                />
                            </div>

                            <div className="mb-4">
                                <label className="block mb-1">Image (optionnel)</label>
                                <input
                                    type="file"
                                    className="w-full p-2 border border-gray-light bg-grey10 rounded"
                                    {...register('image')}
                                    onChange={handleImageChange}  // Déclencher l'aperçu de l'image
                                />
                            </div>

                            {imagePreview && (
                                <div className="mb-4">
                                    <label className="block mb-1">Aperçu de l'image</label>
                                    <img src={imagePreview} alt="Aperçu de l'image" className="w-full h-auto rounded border" />
                                </div>
                            )}

                            <div className="flex justify-end">
                                <button
                                    type="button"
                                    className="px-4 py-2 bg-danger text-white rounded-full mr-2"
                                    onClick={cancel}
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-primary text-white rounded-full"
                                    disabled={isSubmitting || cashOutIsLoad}
                                >
                                    {cashOutIsLoad ? "Traitement..." : "Créer"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
                <div className="mt-4 lg:mt-0 lg:w-full card border p-5 rounded border-grey bg-white">
                    <h1 className="text-lg font-bold mb-5">Mes liens</h1>
                    <table className="table-auto w-full">
                        <thead>
                            <tr className='text-left border border-grey text-sm text-bold bg-grey10'>
                                <th>Date</th>
                                <th>Montant</th>
                                <th>Description</th>
                                <th>Lien</th>
                                <th></th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody className="bg-white">
                            {!isLoad && <>
                                {paymentLinkData.length > 0 && paymentLinkData.map((transactions: any) =>
                                    <React.Fragment key={transactions.id}>
                                        <tr className={`border border-grey text-sm px-4 ${expandedRow === transactions.id ? 'bg-more-bg font-bold' : ''}`} onClick={() => toggleRowExpansion(transactions.id)}>
                                            <td className='text-start'>{formatDateTime(transactions.created_at)}</td>
                                            <td>{transactions.amount} FCFA</td>
                                            <td>{transactions.image_url != null && <img src={transactions.image_url}></img>} {transactions.description}</td>
                                            <td className='to-copy'>{"https://business.seremo.com/#/payment-link/" + transactions.payment_link_uuid}</td>
                                            <td>
                                                <button
                                                    className="px-2 py-1 bg-green text-white rounded flex items-center"
                                                    onClick={() => copyToClipboard("https://business.seremo.com/#/payment-link/" + transactions.payment_link_uuid)}
                                                >
                                                    <LinkIcon width={20} className='mr-2'></LinkIcon>Copier
                                                </button>
                                            </td>
                                            <td>
                                                <button
                                                    className="px-2 py-1 bg-danger text-white rounded"
                                                    onClick={() => deletePaymentLink(transactions.payment_link_uuid)}
                                                >
                                                    <TrashIcon width={20} className='text-white'></TrashIcon>
                                                </button>

                                            </td>
                                        </tr>
                                    </React.Fragment>
                                )}

                                {paymentLinkData.length == 0 && <>
                                    <tr>
                                        <td colSpan={6} className='py-4'>Aucun lien de paiement</td>
                                    </tr>
                                </>}
                            </>}

                            {isLoad && <>
                                <tr><td colSpan={6} className="text-center"></td></tr>
                                <tr><td colSpan={6} className="text-center">Loading...</td></tr>
                                <tr><td colSpan={6} className="text-center"></td></tr>
                            </>}

                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default CreateLink;
